import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDomainStore, useFamilyStore, useFamilyStoriesStore } from '@/hooks/useStores';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import MemoriesFilterBar from '@/components/memories/MemoriesFilterBar';
import MemoriesFeed from '@/components/memories/MemoriesFeed';
import ToneCallout from '@/components/memories/ToneCallout';
import StoryForm, { type StoryFormValues } from '@/components/person/StoryForm';
import MemberFilterTypeahead from '@/components/shared/MemberFilterTypeahead';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import Plus from '@/icons/Plus';
import {
  FAMILY_MEMORIES_TOUR_STEPS,
  FAMILY_MEMORIES_TOUR_STORAGE_KEY,
  resolveFamilyMemoriesTourSteps
} from '@/data/family-memories-tour';
import { canModifyFamily, canModifyFamilyMembers } from '@/utils/rolePermissions';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import { canEditOrDeleteStories, type UpdateStoryPayload } from '@/types/story';
import { hasActiveMemoriesFilters } from '@/types/memories';
import ListResultsBar from '@/components/shared/ListResultsBar';

import './family-memories-screen.css';

const FamilyMemoriesScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const familyStoriesStore = useFamilyStoriesStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [upgradeHint, setUpgradeHint] = useState<string | null>(null);
  const [writableMemberIds, setWritableMemberIds] = useState<Record<string, boolean>>({});

  const familyRole = domainStore.currentFamily?.userRole ?? null;
  const canModifyAnyMember = canModifyFamily(familyRole);
  const canEditDelete = canEditOrDeleteStories(canModifyAnyMember, familyRole);
  const isOwner = isFamilyOwner(familyRole);

  const allMemberOptions = Array.from(familyStore.people.values())
    .map((person) => ({
      id: person.familyMemberId,
      label: person.fullName || `${person.firstName} ${person.lastName}`.trim() || tCommon('unknown')
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // A family role is not the only route to writing a memory: relationship-based access lets someone
  // write about themselves and their close relatives without any family-wide role. Ask the API which
  // members this caller may write for rather than inferring it from the family role.
  const memberOptions = canModifyAnyMember
    ? allMemberOptions
    : allMemberOptions.filter((option) => writableMemberIds[option.id]);
  const canCreateFamilyLevel = canModifyAnyMember || memberOptions.length > 0;

  const memberIdsKey = allMemberOptions.map((option) => option.id).join(',');

  useEffect(() => {
    if (canModifyAnyMember || !memberIdsKey) return;
    const ids = memberIdsKey.split(',');
    let cancelled = false;
    void canModifyFamilyMembers(ids).then((results) => {
      if (!cancelled) setWritableMemberIds(results);
    });
    return () => {
      cancelled = true;
    };
  }, [canModifyAnyMember, memberIdsKey]);

  // Derive on each render — MST array refs are stable; do not wrap in useMemo.
  const authorOptions = familyStoriesStore.authorFilterOptions;

  const selectedMember = selectedMemberId ? familyStore.getPerson(selectedMemberId) : undefined;
  const selectedMemberFirstName =
    selectedMember?.firstName || selectedMember?.fullName?.split(/\s+/)[0] || t('memories.them');

  const loadStories = useCallback(() => {
    if (!familyIdParam) return;
    void familyStoriesStore.searchStories(familyIdParam);
  }, [familyIdParam, familyStoriesStore]);

  useEffect(() => {
    if (!familyIdParam) return;
    domainStore.setSelectedFamily(familyIdParam);
    if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
      void familyStore.loadFamilyData(familyIdParam);
    }
  }, [domainStore, familyStore, familyIdParam]);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  useEffect(() => {
    if (!familyIdParam) return;
    void familyStoriesStore.loadStoryAuthors(familyIdParam);
  }, [familyStoriesStore, familyIdParam]);

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      ...domainStore.currentFamily,
      displayName,
      alternativeName
    });
  };

  const handleCancelShare = () => {
    setIsSharing(false);
    setSelectedMemberId(null);
    setFormError(null);
    setUpgradeHint(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormError(null);
    setUpgradeHint(null);
  };

  const handleShareMemory = async (values: StoryFormValues) => {
    if (!familyIdParam || !selectedMemberId) return;
    setIsSaving(true);
    setFormError(null);
    setUpgradeHint(null);
    const result = await familyStoriesStore.createStory(familyIdParam, {
      familyMemberId: selectedMemberId,
      title: values.title,
      content: values.content,
      mediaAssetIds: values.mediaAssetIds
    });
    setIsSaving(false);
    if (result.success && result.storyId) {
      handleCancelShare();
      navigate(`/families/${familyIdParam}/memories/${result.storyId}`);
    } else {
      const parts = getUpgradeAwareErrorParts(result.error, isOwner, t('memories.createFailed'));
      setFormError(parts.message);
      setUpgradeHint(parts.upgradeHint);
    }
  };

  const handleEdit = async (storyId: string) => {
    if (!familyIdParam) return;
    setIsSharing(false);
    setEditingId(storyId);
    setFormError(null);
    await familyStoriesStore.loadStoryDetail(familyIdParam, storyId);
  };

  const handleUpdate = async (storyId: string, values: StoryFormValues) => {
    if (!familyIdParam) return;
    setIsSaving(true);
    setFormError(null);
    const payload: UpdateStoryPayload = {
      title: values.title,
      content: values.content,
      mediaAssetIds: values.mediaAssetIds
    };
    const result = await familyStoriesStore.updateStory(familyIdParam, storyId, payload);
    setIsSaving(false);
    if (result.success) {
      handleCancelEdit();
    } else {
      setFormError(result.error?.getUserMessage?.() || result.error?.message || tForms('person.updateMemoryFailed'));
    }
  };

  const handleDelete = async (storyId: string) => {
    if (!familyIdParam) return;
    if (!window.confirm(tForms('confirm.deleteMemory'))) return;
    const result = await familyStoriesStore.deleteStory(familyIdParam, storyId);
    if (!result.success) {
      familyStoriesStore.setError(
        result.error?.getUserMessage?.() || result.error?.message || tForms('person.deleteMemoryFailed')
      );
    }
  };

  const familyDisplayName =
    domainStore.currentFamily?.displayName || domainStore.currentFamily?.name || t('reviewQueue.familyFallback');
  const filtersActive = hasActiveMemoriesFilters(familyStoriesStore.filters);
  const hasMemories = familyStoriesStore.total > 0;
  const editingStory = editingId ? familyStoriesStore.getStoryById(editingId) : undefined;
  const feedStories = editingId
    ? familyStoriesStore.items.filter((story) => story.id !== editingId)
    : familyStoriesStore.items.slice();

  const memoriesTour = useScreenTour({
    steps: FAMILY_MEMORIES_TOUR_STEPS,
    completedStorageKey: FAMILY_MEMORIES_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyMemoriesTourSteps(steps, hasMemories)
  });

  if (!familyIdParam) {
    return (
      <div className="family-memories-screen">
        <div className="family-memories-screen__error" role="alert">
          {t('memories.noFamily')}
        </div>
      </div>
    );
  }

  return (
    <div className="family-memories-screen">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('memories.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={memoriesTour.startTour}
        introTourId="memories-intro"
        intro={
          <>
            {t('memories.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          canCreateFamilyLevel ? (
            <button
              type="button"
              className={`btn ${isSharing ? 'btn--ghost' : 'btn--primary'} family-memories-screen__add-btn`}
              data-tour="memories-actions"
              onClick={() => {
                if (isSharing) handleCancelShare();
                else {
                  handleCancelEdit();
                  setIsSharing(true);
                }
              }}
              aria-expanded={isSharing}
            >
              {!isSharing && <Plus width={16} height={16} />}
              {isSharing ? tCommon('cancel') : t('memories.shareMemory')}
            </button>
          ) : undefined
        }
      />

      {canCreateFamilyLevel && isSharing && (
        <div className="family-memories-screen__compose" data-tour="memories-compose">
          <div className="story-form family-memories-screen__member-picker">
            <div className="story-form__field">
              <label className="story-form__label" htmlFor="share-member">
                {t('memories.about')}
              </label>
              <MemberFilterTypeahead
                id="share-member"
                value={selectedMemberId}
                members={memberOptions}
                showAllOption={false}
                fullWidth
                maxResults={5}
                placeholder={t('memories.selectMemberPlaceholder')}
                ariaLabel={t('memories.selectMemberAria')}
                onChange={(memberId) => {
                  setSelectedMemberId(memberId);
                  setFormError(null);
                }}
              />
            </div>
          </div>

          {selectedMemberId && (
            <>
              <ToneCallout firstName={selectedMemberFirstName} userId={domainStore.user?.id} />
              <StoryForm
                key={selectedMemberId}
                familyId={familyIdParam}
                familyMemberId={selectedMemberId}
                isSaving={isSaving}
                errorMessage={formError}
                upgradeHint={upgradeHint}
                onCancel={handleCancelShare}
                onSubmit={handleShareMemory}
              />
            </>
          )}
        </div>
      )}

      {editingId && editingStory?.familyMemberId && (
        <div className="family-memories-screen__compose" data-tour="memories-edit">
          <StoryForm
            key={editingId}
            familyId={familyIdParam}
            familyMemberId={editingStory.familyMemberId}
            isEditing
            isSaving={isSaving}
            errorMessage={formError}
            upgradeHint={upgradeHint}
            initialValues={{
              title: editingStory.title,
              content: editingStory.content,
              mediaAssetIds: editingStory.mediaAttachments.map((a) => a.mediaAssetId),
              mediaMeta: Object.fromEntries(
                editingStory.mediaAttachments.map((a) => [
                  a.mediaAssetId,
                  { mimeType: a.mimeType, filename: a.originalFilename }
                ])
              )
            }}
            onCancel={handleCancelEdit}
            onSubmit={(values) => handleUpdate(editingId, values)}
          />
        </div>
      )}

      <div data-tour="memories-filters">
        <MemoriesFilterBar
          filters={familyStoriesStore.filters}
          members={memberOptions}
          authors={authorOptions}
          onFiltersChange={(filters) => familyStoriesStore.setFilters(filters)}
          onSearch={loadStories}
        />
      </div>

      {familyStoriesStore.error && (
        <div className="family-memories-screen__error" role="alert">
          {familyStoriesStore.error}
        </div>
      )}

      {hasMemories && !familyStoriesStore.isLoading && (
        <ListResultsBar
          countLabel={
            filtersActive
              ? t('memories.matchesCount', { count: feedStories.length })
              : `${t('memories.memoryCount', { count: feedStories.length })} ${t('memories.acrossMembers', {
                  count: familyStoriesStore.uniqueMemberCount
                })}`
          }
        />
      )}

      <div data-tour="memories-feed">
        <MemoriesFeed
          stories={feedStories}
          familyId={familyIdParam}
          isLoading={familyStoriesStore.isLoading}
          canEdit={canEditDelete}
          canDelete={canEditDelete}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyTitle={filtersActive ? t('memories.emptyFilterTitle') : t('memories.emptyTitle')}
          emptyBody={filtersActive ? t('memories.emptyFilterBody') : t('memories.emptyBody')}
        />
      </div>

      {familyIdParam && domainStore.currentFamily && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={domainStore.currentFamily.displayName || domainStore.currentFamily.name}
          canonicalName={domainStore.currentFamily.name}
          hasAlternative={!!domainStore.currentFamily.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={memoriesTour} />
    </div>
  );
});

export default FamilyMemoriesScreen;
